import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SignalForm } from '@/components/signals';
import { useSignals, useTelegram } from '@/hooks';
import type { SignalFormData } from '@/components/signals/SignalForm';

export const EditSignalPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { signals, updateSignal, isLoading, error } = useSignals();
  const { showBackButton, hideBackButton, hideMainButton, showAlert } = useTelegram();

  const signal = useMemo(() => signals.find((s) => s.id === id), [signals, id]);

  // Redirect if signal not found
  useEffect(() => {
    if (!signal && signals.length > 0) {
      navigate('/signals', { replace: true });
    }
  }, [signal, signals.length, navigate]);

  // Set up back button
  useEffect(() => {
    showBackButton(() => navigate(-1));
    hideMainButton();

    return () => {
      hideBackButton();
    };
  }, [showBackButton, hideBackButton, hideMainButton, navigate]);

  const handleSubmit = async (data: SignalFormData) => {
    if (!id || !data.asset) return;

    const result = await updateSignal(id, {
      symbol: data.asset.symbol,
      quoteCurrency: data.quoteCurrency,
      type: data.type,
      value: parseFloat(data.value),
      direction: data.direction,
      repeat: data.repeat,
      isEdit: true,
    });

    if (result) {
      showAlert('Alert updated successfully!');
      navigate(`/signals/${id}`, { replace: true });
    }
  };

  if (!signal) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--tg-theme-bg-color)',
        }}
      >
        <p style={{ color: 'var(--tg-theme-hint-color)' }}>Loading...</p>
      </div>
    );
  }

  // Prepare initial data
  const initialData: Partial<SignalFormData> = {
    asset: signal.asset || null,
    quoteCurrency: signal.quoteCurrency,
    type: signal.type,
    value: signal.value.toString(),
    direction: [signal.direction],
    repeat: signal.repeat,
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--tg-theme-bg-color)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px',
          borderBottom: '1px solid var(--tg-theme-secondary-bg-color)',
        }}
      >
        <h1
          style={{
            fontSize: '20px',
            fontWeight: 700,
            color: 'var(--tg-theme-text-color)',
            margin: 0,
          }}
        >
          Edit Alert
        </h1>
      </div>

      {/* Error display */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: 'rgba(255, 59, 48, 0.1)',
            color: '#ff3b30',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}

      {/* Form */}
      <SignalForm
        initialData={initialData}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        submitLabel="Save Changes"
      />
    </div>
  );
};
